import React, { useMemo, useState } from 'react';
import { toLocalYMD } from '../lib/dates';
import { 
  ArrowLeft, Receipt, Search, Plus, Download, Calendar,
  Tag, Car, Fuel, Wrench, ShoppingBag, Coffee, MoreVertical,
  TrendingUp, Filter, Camera, Edit, Trash2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const Expenses = ({ onBack }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [dateRange, setDateRange] = useState('month');

  // Mock expenses data
  const expenses = useMemo(() => {
    const categories = [
      { id: 'fuel', name: 'Fuel', icon: Fuel, color: '#f59e0b' },
      { id: 'equipment', name: 'Equipment', icon: Wrench, color: '#3b82f6' },
      { id: 'vehicle', name: 'Vehicle', icon: Car, color: '#10b981' },
      { id: 'supplies', name: 'Supplies', icon: ShoppingBag, color: '#8b5cf6' },
      { id: 'food', name: 'Food & Drink', icon: Coffee, color: '#ec4899' },
      { id: 'other', name: 'Other', icon: Tag, color: '#6b7280' }
    ];

    const expenseData = [
      { desc: 'Diesel - Shell Burgess Hill', cat: 'fuel', amount: 85.50 },
      { desc: 'Water Fed Pole Brushes', cat: 'equipment', amount: 45.00 },
      { desc: 'Van MOT', cat: 'vehicle', amount: 54.85 },
      { desc: 'Window Cleaning Supplies', cat: 'supplies', amount: 18.50 },
      { desc: 'Lunch - Costa Coffee', cat: 'food', amount: 8.95 },
      { desc: 'Diesel - Tesco Haywards Heath', cat: 'fuel', amount: 72.30 },
      { desc: 'T-Cut & Polish', cat: 'supplies', amount: 12.99 },
      { desc: 'Van Insurance Monthly', cat: 'vehicle', amount: 125.00 },
      { desc: 'Pure Water System Filter', cat: 'equipment', amount: 35.00 },
      { desc: 'Parking - Brighton', cat: 'other', amount: 8.00 },
      { desc: 'Diesel - BP Hassocks', cat: 'fuel', amount: 78.45 },
      { desc: 'New Ladder Straps', cat: 'equipment', amount: 22.50 },
      { desc: 'Window Cleaning Solution 5L', cat: 'supplies', amount: 15.99 },
      { desc: 'Tea & Snacks', cat: 'food', amount: 6.50 },
      { desc: 'Van Wash', cat: 'vehicle', amount: 12.00 },
    ];

    return expenseData.map((exp, idx) => {
      const category = categories.find(c => c.id === exp.cat);
      const daysAgo = Math.floor(Math.random() * 30);
      const date = new Date();
      date.setDate(date.getDate() - daysAgo);
      
      return {
        id: `EXP-${String(idx + 1).padStart(4, '0')}`,
        description: exp.desc,
        category: category,
        amount: exp.amount,
        date: toLocalYMD(date),
        receipt: Math.random() > 0.3,
        assignee: Math.random() > 0.5 ? 'Dan' : 'Steve'
      };
    }).sort((a, b) => new Date(b.date) - new Date(a.date));
  }, []);

  const categories = [
    { id: 'all', name: 'All', icon: Tag, color: '#6b7280' },
    { id: 'fuel', name: 'Fuel', icon: Fuel, color: '#f59e0b' },
    { id: 'equipment', name: 'Equipment', icon: Wrench, color: '#3b82f6' },
    { id: 'vehicle', name: 'Vehicle', icon: Car, color: '#10b981' },
    { id: 'supplies', name: 'Supplies', icon: ShoppingBag, color: '#8b5cf6' },
    { id: 'food', name: 'Food', icon: Coffee, color: '#ec4899' },
    { id: 'other', name: 'Other', icon: Tag, color: '#6b7280' }
  ];

  const filteredExpenses = useMemo(() => {
    return expenses.filter(exp => {
      const matchesSearch = exp.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = categoryFilter === 'all' || exp.category.id === categoryFilter;
      return matchesSearch && matchesCategory;
    });
  }, [expenses, searchTerm, categoryFilter]);

  const stats = useMemo(() => {
    const total = expenses.reduce((sum, exp) => sum + exp.amount, 0);
    const byCategory = categories.slice(1).map(cat => ({
      ...cat,
      total: expenses.filter(exp => exp.category.id === cat.id).reduce((sum, exp) => sum + exp.amount, 0)
    })).sort((a, b) => b.total - a.total);
    
    return { total, byCategory };
  }, [expenses]);

  return (
    <div className="h-full flex flex-col bg-gray-50" data-testid="expenses-page">
      {/* Header */}
      <div className="bg-zinc-800 text-white px-4 py-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            {onBack && (
              <button 
                onClick={onBack}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                data-testid="expenses-back-btn"
              >
                <ArrowLeft size={20} />
              </button>
            )}
            <h1 className="text-xl font-semibold">Expenses</h1>
          </div>
          <div className="flex items-center gap-2">
            <select 
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-sm"
            >
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="quarter">This Quarter</option>
              <option value="year">This Year</option>
            </select>
            <button 
              className="flex items-center gap-2 bg-pink-500 hover:bg-pink-600 px-4 py-2 rounded-lg transition-colors"
              data-testid="add-expense-btn"
            >
              <Plus size={18} />
              Add Expense
            </button>
          </div>
        </div>

        {/* Total and Export */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-white/70 text-sm">Total Expenses</p>
            <p className="text-3xl font-bold">£{stats.total.toFixed(2)}</p>
          </div>
          <button className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-lg transition-colors">
            <Download size={18} />
            Export
          </button>
        </div>
      </div>

      {/* Category Summary */}
      <div className="p-4 bg-white border-b border-gray-200">
        <div className="flex items-center gap-2 overflow-x-auto pb-2">
          {categories.map(cat => {
            const Icon = cat.icon;
            const isActive = categoryFilter === cat.id;
            const catTotal = cat.id === 'all' ? stats.total : stats.byCategory.find(c => c.id === cat.id)?.total || 0;
            
            return (
              <button
                key={cat.id}
                onClick={() => setCategoryFilter(cat.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${
                  isActive 
                    ? 'bg-pink-500 text-white' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                data-testid={`category-${cat.id}`}
              >
                <Icon size={16} style={{ color: isActive ? '#fff' : cat.color }} />
                <span className="text-sm font-medium">{cat.name}</span>
                <span className={`text-xs ${isActive ? 'text-white/80' : 'text-gray-400'}`}>
                  £{catTotal.toFixed(0)}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Search */}
      <div className="p-4 bg-white border-b border-gray-200">
        <div className="relative">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search expenses..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
            data-testid="expense-search"
          />
        </div>
      </div>

      {/* Expense List */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="space-y-2">
          {filteredExpenses.map(expense => {
            const Icon = expense.category.icon;
            return (
              <div 
                key={expense.id}
                className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-all cursor-pointer"
                data-testid={`expense-${expense.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${expense.category.color}20` }}
                    >
                      <Icon size={20} style={{ color: expense.category.color }} />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{expense.description}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-gray-500">{expense.date}</span>
                        <span className="text-xs text-gray-300">•</span>
                        <span 
                          className="text-xs px-2 py-0.5 rounded-full"
                          style={{ backgroundColor: `${expense.category.color}20`, color: expense.category.color }}
                        >
                          {expense.category.name}
                        </span>
                        <span className="text-xs text-gray-300">•</span>
                        <span className="text-xs text-gray-500">{expense.assignee}</span>
                        {expense.receipt && (
                          <>
                            <span className="text-xs text-gray-300">•</span>
                            <span className="text-xs text-green-600 flex items-center gap-1">
                              <Camera size={10} />
                              Receipt
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-right flex items-center gap-3">
                    <p className="text-lg font-bold text-gray-900">£{expense.amount.toFixed(2)}</p>
                    <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                      <MoreVertical size={16} className="text-gray-400" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {filteredExpenses.length === 0 && (
          <div className="text-center py-12">
            <Receipt size={48} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">No expenses found</p>
            <p className="text-sm text-gray-400">Try adjusting your search or filter</p>
          </div>
        )}
      </div>

      {/* Category Breakdown Card */}
      <div className="p-4 bg-white border-t border-gray-200">
        <p className="text-sm font-medium text-gray-700 mb-3">Spending by Category</p>
        <div className="flex gap-2">
          {stats.byCategory.slice(0, 4).map(cat => {
            const percentage = (cat.total / stats.total) * 100;
            return (
              <div 
                key={cat.id}
                className="flex-1 h-2 rounded-full"
                style={{ backgroundColor: `${cat.color}30` }}
              >
                <div 
                  className="h-full rounded-full transition-all"
                  style={{ width: `${percentage}%`, backgroundColor: cat.color }}
                />
              </div>
            );
          })}
        </div>
        <div className="flex justify-between mt-2 text-xs text-gray-500">
          {stats.byCategory.slice(0, 4).map(cat => (
            <span key={cat.id} style={{ color: cat.color }}>{cat.name}</span>
          ))}
        </div>
      </div>

      {/* FAB - Fixed bottom LEFT */}
      <button 
        className="fixed bottom-6 left-6 w-14 h-14 bg-pink-500 hover:bg-pink-600 rounded-full shadow-lg flex items-center justify-center transition-colors z-50"
        data-testid="fab-add-expense"
      >
        <Plus size={24} className="text-white" />
      </button>
    </div>
  );
};

export default Expenses;
